import { useState } from 'react';
import { ApiError, api } from '../api/client';
import type { User } from '../api/types';
import { IconEye, IconEyeOff } from '../components/Icons';
import { LogoBadge } from '../components/Logo';
import { useTheme } from '../components/Toast';
import { useT } from '../i18n';

export function Register({
  onRegistered,
  onBackToLogin,
}: {
  onRegistered: (user: User) => void;
  onBackToLogin: () => void;
}) {
  const { t, lang, setLang } = useT();
  useTheme();

  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onRegistered(
        await api.register({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          phone: form.phone.trim() || undefined,
        }),
      );
    } catch (cause) {
      // The server explains the duplicate-email and rate-limit cases in words.
      setError(cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <LogoBadge size={52} />
          <h1>{t('createAccount')}</h1>
        </div>

        <label>
          {t('name')}
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            minLength={2}
            autoComplete="name"
          />
        </label>

        <label>
          {t('email')}
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            dir="ltr"
            autoComplete="username"
          />
        </label>

        <label>
          {t('phone')}
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            dir="ltr"
            inputMode="tel"
            autoComplete="tel"
          />
        </label>

        <label>
          {t('password')}
          <span className="input-with-action">
            <input
              type={reveal ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
              dir="ltr"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="reveal-btn"
              onClick={() => setReveal((v) => !v)}
              aria-label={reveal ? t('hidePassword') : t('showPassword')}
            >
              {reveal ? <IconEyeOff /> : <IconEye />}
            </button>
          </span>
        </label>
        <span className="muted tiny">{t('passwordHint')}</span>

        {error && <p className="form-error">{error}</p>}

        <button type="submit" className="btn primary block" disabled={busy}>
          {busy && <span className="btn-spinner" />}
          {busy ? t('loading') : t('createAccount')}
        </button>

        <div className="login-footer">
          <button type="button" className="link-btn" onClick={onBackToLogin}>
            {t('haveAccount')}
          </button>
          <button
            type="button"
            className="link-btn"
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
          >
            {lang === 'ar' ? 'English' : 'العربية'}
          </button>
        </div>
      </form>
    </div>
  );
}
